import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import DeltaChip from './DeltaChip';
import InlineTrend from './InlineTrend';
import { getMetricStatusColor } from './metricStatus';

interface VolumeCardProps {
  metricName: string;
  owner: string;
  goal: number;
  /** "GTE" (default) | "LTE" — picks the goal comparator (≥ / ≤). */
  goalDirection?: string;
  /** Display unit for the goal ('' = unitless) — catalog-driven (metricUnit). */
  unit?: string;
  /** null entries = weeks with no observations (distinct from a real 0). */
  weeklyVolumes: Array<number | null>;
  weeklyAttainment: Array<number | null>;
  weekIndex: number;
  weeks: string[];
}

function formatVolume(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toLocaleString();
}

const VolumeCard: React.FC<VolumeCardProps> = ({
  metricName,
  owner,
  goal,
  goalDirection,
  unit,
  weeklyVolumes,
  weeklyAttainment,
  weekIndex,
  weeks,
}) => {
  const inRange = weekIndex >= 0 && weekIndex < weeklyVolumes.length;
  const volume = inRange
    ? weeklyVolumes[weekIndex]
    : weeklyVolumes[weeklyVolumes.length - 1] ?? null;
  const attainmentRaw =
    weeklyAttainment.length > 0 ? weeklyAttainment[weekIndex] ?? null : null;
  const hasAttainment = weeklyAttainment.length > 0 && attainmentRaw !== null;
  const attainment = attainmentRaw ?? 0;
  // The delta compares whatever the headline shows: attainment (pp) when the
  // card has one, raw volume otherwise. A no-data week (null volume) keeps
  // the chip on "no data", matching the "—" headline. Previous = last week
  // WITH data, so a gap never looks like "no change".
  const deltaSeries = hasAttainment ? weeklyAttainment : weeklyVolumes;
  const deltaCurrent =
    volume === null ? null : hasAttainment ? attainment : volume;
  let deltaPrevious: number | null = null;
  for (let i = Math.min(weekIndex, deltaSeries.length) - 1; i >= 0; i--) {
    const candidate = deltaSeries[i];
    if (typeof candidate === 'number') {
      deltaPrevious = candidate;
      break;
    }
  }
  const noData = volume === null;
  const color = noData
    ? '#8c8c94'
    : hasAttainment
    ? getMetricStatusColor(attainment, goal)
    : '#414d5c';

  return (
    <div className="volume-card" style={{ borderLeftColor: color }}>
      <Box variant="p" fontSize="body-s" fontWeight="bold" color="text-label">
        {metricName}
      </Box>

      <div className="volume-card__body">
        <div className="volume-card__numbers">
          {noData ? (
            <Box variant="div" fontSize="heading-xl" fontWeight="heavy">
              <span style={{ color }}>—</span>
            </Box>
          ) : hasAttainment ? (
            <>
              <Box variant="div" fontSize="heading-xl" fontWeight="heavy">
                <span style={{ color }}>{attainment.toFixed(0)}%</span>
              </Box>
              <Box variant="p" fontSize="body-s" color="text-body-secondary">
                {formatVolume(volume)}
              </Box>
            </>
          ) : (
            <Box variant="div" fontSize="heading-xl" fontWeight="heavy">
              {formatVolume(volume)}
            </Box>
          )}
        </div>
        {hasAttainment ? (
          <InlineTrend
            values={weeklyAttainment}
            goal={goal}
            color={color}
            selectedIndex={weekIndex}
            weekLabels={weeks}
          />
        ) : (
          <InlineTrend
            values={weeklyVolumes}
            color={color}
            formatValue={(v) => `${(v / 1000).toFixed(0)}K`}
            selectedIndex={weekIndex}
            weekLabels={weeks}
          />
        )}
      </div>

      {/* Goal unit is catalog-driven: an attainment card's goal is on the %
          scale by construction; otherwise the metric's own unit applies —
          "%"" glues to the number, word units ("days") get a space, unitless
          stays bare (was hardcoded "%", wrong for any raw-volume goal). */}
      <div className="volume-card__footer">
        <DeltaChip
          current={deltaCurrent}
          previous={deltaPrevious}
          unit={hasAttainment ? 'pp' : 'volume'}
        />
        <Box variant="span" fontSize="body-s" color="text-status-inactive">
          Owner: {owner}
          {goal > 0
            ? ` · Goal: ${
                goalDirection === 'LTE' ? '≤' : '≥'
              } ${goal.toLocaleString()}${
                weeklyAttainment.length > 0 || unit === '%'
                  ? '%'
                  : unit
                  ? ` ${unit}`
                  : ''
              }`
            : ''}
        </Box>
      </div>
    </div>
  );
};

export default VolumeCard;
